import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axiosInstance from "../../../Axiosinstance";

const PartnerUserSchema = Yup.object().shape({
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
  email: Yup.string()
    .trim()
    .email("Enter a valid email address")
    .required("Email is required"),
  password: Yup.string()
    .trim()
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&^#()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
      "Password must be at least 8 characters and include uppercase, lowercase, number, and special character"
    )
    .required("Password is required"),
  phone: Yup.string()
    .trim()
    .matches(/^\d{10}$/, "Enter a valid 10-digit phone number")
    .required("Phone is required"),
  countryCode: Yup.string()
    .oneOf(["+91"], "Country code must be +91")
    .required("Country code is required"),
  company_uid: Yup.string().trim().required("Company is required"),
  branch_uid: Yup.string().trim().required("Branch is required"),
});

const fieldClasses = (errors, touched) =>
  "form-control" + (errors && touched ? " is-invalid" : "");

const CompanyPartnerUsers = () => {
  const [companies, setCompanies] = useState([]);
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPartnerUser, setSelectedPartnerUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    fetchPartnerUsersAndCompanies(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch]);

  const fetchPartnerUsersAndCompanies = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const [companyRes, adminsRes] = await Promise.all([
        axiosInstance.get("/api/getcompanydetails"),
        axiosInstance.post("/api/getPartnerAdmins", {
          search,
          page,
          limit: 10,
        }),
      ]);

      if (companyRes?.data?.status) {
        setCompanies(companyRes.data.companies || []);
      } else {
        toast.error("Unable to fetch companies");
      }

      const admins = adminsRes?.data?.data || [];
      setPartnerUsers(admins);
      setTotalCount(adminsRes?.data?.totalCount ?? 0);
      setTotalPages(adminsRes?.data?.pagination?.totalPages ?? 1);
    } catch (error) {
      console.error(error);
      toast.error("Unable to load partner users and companies");
    } finally {
      setLoading(false);
    }
  };

  const getCompanyLabel = (companyUid) => {
    const company = companies.find((item) => item.company_uid === companyUid);
    return company?.company_name || "Unknown Company";
  };

  const getBranchLabel = (companyUid, branchUid) => {
    const company = companies.find((item) => item.company_uid === companyUid);
    const branch = company?.branches?.find(
      (b) => b.branch_uid === branchUid || b._id?.toString() === branchUid
    );
    return branch?.branch_name || "Unknown Branch";
  };

  const branchOptionsByCompany = useMemo(() => {
    return (companyUid) => {
      const company = companies.find((item) => item.company_uid === companyUid);
      return company?.branches || [];
    };
  }, [companies]);

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        ...values,
        type: "partner",
        accessPermission: [],
      };

      const { data } = await axiosInstance.post("/api/branchuser", payload);

      if (data?.status && !data?.isError) {
        toast.success(data?.status || "Partner user created successfully");
        resetForm();
        setIsModalOpen(false);
        fetchPartnerUsersAndCompanies();
      } else {
        toast.error(
          data?.message || data?.error || "Unable to create partner user"
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Unable to create partner user"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardBody>
        <Row className="mb-3 align-items-center">
          <Col md="6" className="mb-2 mb-md-0">
            <h4 className="card-title mb-0">Partner Users</h4>
          </Col>
          <Col md="6" className="text-md-end text-start">
            <Button
              color="primary"
              className="font-16 btn btn-primary px-4 shadow-sm"
              onClick={toggleModal}
              style={{ borderRadius: "999px", minWidth: "160px" }}
            >
              Create Partner User
            </Button>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md="4">
            <input
              type="search"
              className="form-control"
              placeholder="Search partner users"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Col>
        </Row>

        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Company</th>
                <th>Branch</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>Loading...</td>
                </tr>
              ) : partnerUsers.length === 0 ? (
                <tr>
                  <td colSpan={6}>No partner users found</td>
                </tr>
              ) : (
                partnerUsers.map((user) => (
                  <tr key={user.uid || user._id}>
                    <td>
                      {user.first_name} {user.last_name}
                    </td>
                    <td>{user.email}</td>
                    <td>
                      {user.phonecountrycode || ""} {user.phone}
                    </td>
                    <td>{getCompanyLabel(user.company_uid)}</td>
                    <td>{getBranchLabel(user.company_uid, user.branch_uid)}</td>
                    <td>
                      <Button
                        color="light"
                        size="sm"
                        onClick={() => {
                          setSelectedPartnerUser(user);
                          setIsEditModalOpen(true);
                        }}
                        title="Edit partner user"
                      >
                        <span role="img" aria-label="edit">
                          ✎
                        </span>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted">
            {totalCount} partner user{totalCount === 1 ? "" : "s"}
          </div>
          <div>
            <Button
              color="light"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              Previous
            </Button>
            <Button
              color="light"
              size="sm"
              className="ms-2"
              disabled={currentPage >= totalPages}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
            >
              Next
            </Button>
            <span className="ms-3 text-muted">
              Page {currentPage} of {totalPages}
            </span>
          </div>
        </div>

        <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
          <ModalHeader toggle={toggleModal}>Create Partner User</ModalHeader>
          <ModalBody>
            <Formik
              initialValues={{
                firstName: "",
                lastName: "",
                email: "",
                password: "",
                phone: "",
                countryCode: "+91",
                company_uid: "",
                branch_uid: "",
              }}
              validationSchema={PartnerUserSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, setFieldValue, isSubmitting }) => {
                const branchOptions = branchOptionsByCompany(
                  values.company_uid
                );
                return (
                  <Form>
                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">First Name *</Label>
                          <Field
                            name="firstName"
                            type="text"
                            className={fieldClasses(
                              errors.firstName,
                              touched.firstName
                            )}
                          />
                          <ErrorMessage
                            name="firstName"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Last Name *</Label>
                          <Field
                            name="lastName"
                            type="text"
                            className={fieldClasses(
                              errors.lastName,
                              touched.lastName
                            )}
                          />
                          <ErrorMessage
                            name="lastName"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Email *</Label>
                          <Field
                            name="email"
                            type="email"
                            className={fieldClasses(
                              errors.email,
                              touched.email
                            )}
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Password *</Label>
                          <Field
                            name="password"
                            type="password"
                            className={fieldClasses(
                              errors.password,
                              touched.password
                            )}
                          />
                          <ErrorMessage
                            name="password"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="4">
                        <div className="mb-3">
                          <Label className="form-label">Country Code *</Label>
                          <Field
                            name="countryCode"
                            type="text"
                            className={fieldClasses(
                              errors.countryCode,
                              touched.countryCode
                            )}
                            value="+91"
                            readOnly
                          />
                          <ErrorMessage
                            name="countryCode"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                      <Col md="8">
                        <div className="mb-3">
                          <Label className="form-label">Phone *</Label>
                          <Field
                            name="phone"
                            type="tel"
                            className={fieldClasses(
                              errors.phone,
                              touched.phone
                            )}
                          />
                          <ErrorMessage
                            name="phone"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Company *</Label>
                          <Field
                            name="company_uid"
                            as="select"
                            className={fieldClasses(
                              errors.company_uid,
                              touched.company_uid
                            )}
                            onChange={(e) => {
                              setFieldValue("company_uid", e.target.value);
                              setFieldValue("branch_uid", "");
                            }}
                          >
                            <option value="">Select company</option>
                            {companies.map((company) => (
                              <option
                                key={company.company_uid}
                                value={company.company_uid}
                              >
                                {company.company_name}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="company_uid"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Branch *</Label>
                          <Field
                            name="branch_uid"
                            as="select"
                            className={fieldClasses(
                              errors.branch_uid,
                              touched.branch_uid
                            )}
                            disabled={
                              !values.company_uid || branchOptions.length === 0
                            }
                          >
                            <option value="">
                              {values.company_uid
                                ? branchOptions.length > 0
                                  ? "Select branch"
                                  : "No branches available"
                                : "Select a company first"}
                            </option>
                            {branchOptions.map((branch) => (
                              <option
                                key={branch.branch_uid || branch._id}
                                value={branch.branch_uid || branch._id}
                              >
                                {branch.branch_name}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="branch_uid"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                    </Row>

                    <ModalFooter>
                      <Button
                        color="secondary"
                        onClick={toggleModal}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        Create User
                      </Button>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={isEditModalOpen}
          toggle={() => setIsEditModalOpen(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setIsEditModalOpen(false)}>
            Edit Partner User
          </ModalHeader>
          <ModalBody>
            {selectedPartnerUser && (
              <Formik
                enableReinitialize
                initialValues={{
                  firstName: selectedPartnerUser.first_name || "",
                  lastName: selectedPartnerUser.last_name || "",
                  phone: selectedPartnerUser.phone || "",
                  countryCode: selectedPartnerUser.phonecountrycode || "+91",
                  email: selectedPartnerUser.email || "",
                  company_uid: selectedPartnerUser.company_uid || "",
                  branch_uid: selectedPartnerUser.branch_uid || "",
                }}
                validationSchema={Yup.object().shape({
                  firstName: Yup.string()
                    .trim()
                    .required("First name is required"),
                  lastName: Yup.string()
                    .trim()
                    .required("Last name is required"),
                  phone: Yup.string()
                    .trim()
                    .matches(
                      /^[0-9]{10}$/,
                      "Enter a valid 10-digit phone number"
                    )
                    .required("Phone is required"),
                  countryCode: Yup.string().oneOf(["+91"]).required(),
                })}
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    const { data } = await axiosInstance.post(
                      `/api/updatePartnerUser/${selectedPartnerUser.uid}`,
                      {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        phone: values.phone,
                        countryCode: values.countryCode,
                      }
                    );

                    if (data?.status) {
                      toast.success(data.msg || "Partner user updated");
                      setIsEditModalOpen(false);
                      setSelectedPartnerUser(null);
                      fetchPartnerUsersAndCompanies(
                        currentPage,
                        debouncedSearch
                      );
                    } else {
                      toast.error(
                        data?.message ||
                          data?.msg ||
                          "Unable to update partner user"
                      );
                    }
                  } catch (error) {
                    console.error(error);
                    toast.error(
                      error?.response?.data?.message ||
                        "Unable to update partner user"
                    );
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ values, errors, touched, isSubmitting }) => (
                  <Form>
                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">First Name *</Label>
                          <Field
                            name="firstName"
                            type="text"
                            className={fieldClasses(
                              errors.firstName,
                              touched.firstName
                            )}
                          />
                          <ErrorMessage
                            name="firstName"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Last Name *</Label>
                          <Field
                            name="lastName"
                            type="text"
                            className={fieldClasses(
                              errors.lastName,
                              touched.lastName
                            )}
                          />
                          <ErrorMessage
                            name="lastName"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="4">
                        <div className="mb-3">
                          <Label className="form-label">Country Code *</Label>
                          <Field
                            name="countryCode"
                            type="text"
                            className={fieldClasses(
                              errors.countryCode,
                              touched.countryCode
                            )}
                            value="+91"
                            readOnly
                          />
                        </div>
                      </Col>
                      <Col md="8">
                        <div className="mb-3">
                          <Label className="form-label">Phone *</Label>
                          <Field
                            name="phone"
                            type="tel"
                            className={fieldClasses(
                              errors.phone,
                              touched.phone
                            )}
                          />
                          <ErrorMessage
                            name="phone"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Email</Label>
                          <Field
                            name="email"
                            type="email"
                            className="form-control"
                            disabled
                          />
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Company</Label>
                          <Field
                            name="company_uid"
                            type="text"
                            className="form-control"
                            disabled
                            value={getCompanyLabel(values.company_uid)}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Branch</Label>
                          <Field
                            name="branch_uid"
                            type="text"
                            className="form-control"
                            disabled
                            value={getBranchLabel(
                              values.company_uid,
                              values.branch_uid
                            )}
                          />
                        </div>
                      </Col>
                    </Row>

                    <ModalFooter>
                      <Button
                        color="secondary"
                        onClick={() => setIsEditModalOpen(false)}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        Save Changes
                      </Button>
                    </ModalFooter>
                  </Form>
                )}
              </Formik>
            )}
          </ModalBody>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default CompanyPartnerUsers;
